import React, { useEffect, useMemo } from "react";
import { ArrowRight, Clock3, Flame, Leaf, Utensils } from "lucide-react";
import { Helmet } from "react-helmet-async";
import PostsShowOnHome from "../../widgets/PostsShowOnHome";
import { message } from "antd";
import ShowPostInfiniteScroll from "../../widgets/ShowPostInfiniteScroll";
import { Alert, Snackbar } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import SiteFooter from "../Footer/SiteFooter";
import FoodSearch from "../utils/FoodSearch";

function Home() {
  const [handleSnackbarValue, setHandleSnackbarValue] = React.useState(false);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const status = searchParams.get("status");

  const GERD = "GERD-Friendly";
  const IBS = "IBS-Friendly";
  const Gastritis = "Gastritis-Friendly";
  const Barrett = "Barretts-Esophagus-Friendly";

  const seoData = useMemo(
    () => ({
      title:
        "GERD, Acid Reflux & IBS-Friendly Recipes | GentleGourmet",
      description:
        "Evidence-informed recipes for GERD, acid reflux, IBS, gastritis and Barrett's esophagus. Low-acid, low-FODMAP and gut-friendly meals you can actually enjoy.",
      keywords:
        "GERD recipes, acid reflux recipes, IBS friendly recipes, low FODMAP meals, gastritis diet recipes, Barrett's esophagus recipes, low acid recipes, silent reflux diet, gut friendly meals, heartburn diet recipes",
      canonicalUrl: "https://gentlegourmet.app/",
      ogImage: "https://gentlegourmet.app/images/og-home.jpg",
      siteName: "GentleGourmet",
      brand: "GentleGourmet",
      locale: "en_US",
      twitterHandle: "@gentlegourmetapp",
    }),
    []
  );

  const editorialCollections = useMemo(
    () => [
      {
        title: "30-minute low-acid dinners",
        copy: "Weeknight meals gentle on reflux and ready fast.",
        icon: <Clock3 className="h-5 w-5" aria-hidden="true" />,
        href: "/search?search_query=quick&calorieTotal=0%20TO%2050000",
      },
      {
        title: "Soothing soups & broths",
        copy: "Comfort bowls for flare-up days and gentle recovery.",
        icon: <Flame className="h-5 w-5" aria-hidden="true" />,
        href: "/search?search_query=soup&calorieTotal=0%20TO%2050000",
      },
      {
        title: "Low-FODMAP lunches",
        copy: "Balanced midday meals that keep IBS symptoms calm.",
        icon: <Leaf className="h-5 w-5" aria-hidden="true" />,
        href: "/search?search_query=lunch&calorieTotal=0%20TO%2050000",
      },
      {
        title: "Meal-prep favourites",
        copy: "Batch-cook recipes built for consistency and comfort.",
        icon: <Utensils className="h-5 w-5" aria-hidden="true" />,
        href: "/search?search_query=meal+prep&calorieTotal=0%20TO%2050000",
      },
    ],
    []
  );

  const recipeCategories = useMemo(
    () => [
      {
        id: "gerd",
        title: "GERD & Acid Reflux",
        tagline: "Low-acid recipes that still taste like dinner.",
        category: GERD,
        path: "/explore/GERD-Friendly/all",
        description:
          "Low-acid, low-fat recipes designed to reduce reflux triggers without compromising flavour. Ideal for anyone managing GERD, LPR (silent reflux), or chronic heartburn.",
      },
      {
        id: "ibs",
        title: "IBS & Low-FODMAP",
        tagline: "Gentle, low-FODMAP meals for calmer digestion.",
        category: IBS,
        path: "/explore/IBS-Friendly/all",
        description:
          "Low-FODMAP recipes and IBS-friendly comfort food built around what's easy to tolerate during flare-ups and everyday eating.",
      },
      {
        id: "gastritis",
        title: "Gastritis",
        tagline: "Soothing meals for sensitive, inflamed stomachs.",
        category: Gastritis,
        path: "/explore/Gastritis-Friendly/all",
        description:
          "Nourishing, gently seasoned recipes to support gastritis healing &mdash; skip the alcohol, caffeine and acidic triggers without skipping flavour.",
      },
      {
        id: "barrett",
        title: "Barrett's Esophagus",
        tagline: "Careful, comforting food for esophageal health.",
        category: Barrett,
        path: "/explore/Barretts-Esophagus-Friendly/all",
        description:
          "Recipes curated with esophageal care in mind: smooth textures, low-acid ingredients, and anti-inflammatory staples.",
      },
    ],
    []
  );

  useEffect(() => {
    if (status === "logout") message.success("Logged out successfully");
    if (status === "error" || status === "success")
      setHandleSnackbarValue(true);
  }, [status]);

  const handleSnackBarClose = (_event, reason) => {
    if (reason === "clickaway") return;
    setHandleSnackbarValue(false);
  };

  const scrollToRecipes = () => {
    const el = document.getElementById("recipes-start");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Structured Data
  const jsonLdWebsite = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: seoData.siteName,
    description: seoData.description,
    url: seoData.canonicalUrl,
    potentialAction: {
      "@type": "SearchAction",
      target: `${seoData.canonicalUrl}search?search_query={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
    publisher: {
      "@type": "Organization",
      name: seoData.brand,
      url: seoData.canonicalUrl,
      logo: {
        "@type": "ImageObject",
        url: "https://gentlegourmet.app/images/logo.png",
      },
    },
  };

  const jsonLdWebPage = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: seoData.title,
    url: seoData.canonicalUrl,
    description: seoData.description,
    inLanguage: "en-US",
    primaryImageOfPage: {
      "@type": "ImageObject",
      url: seoData.ogImage,
    },
    isPartOf: {
      "@type": "WebSite",
      name: seoData.siteName,
      url: seoData.canonicalUrl,
    },
    about: [
      { "@type": "Thing", name: "Gastroesophageal reflux disease" },
      { "@type": "Thing", name: "Acid reflux diet" },
      { "@type": "Thing", name: "Irritable bowel syndrome diet" },
      { "@type": "Thing", name: "Gastritis diet" },
      { "@type": "Thing", name: "Barrett's esophagus diet" },
      { "@type": "Thing", name: "Low-FODMAP diet" },
    ],
  };

  const jsonLdOrganization = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: seoData.brand,
    url: seoData.canonicalUrl,
    logo: "https://gentlegourmet.app/images/logo.png",
  };

  const jsonLdBreadcrumbs = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: seoData.canonicalUrl,
      },
    ],
  };

  const jsonLdItemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Digestive-friendly recipe collections",
    itemListElement: recipeCategories.map((categoryData, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: categoryData.title,
      url: `https://gentlegourmet.app${categoryData.path}`,
    })),
  };

  const faqItems = [
    {
      q: "What foods should I avoid with GERD or acid reflux?",
      a: "Common reflux triggers include high-fat fried foods, raw onion and garlic, citrus, tomato, chocolate, coffee, alcohol, peppermint, and carbonated drinks. Everyone is different, so use our recipes as a gentler starting point and keep a simple food journal to identify your personal triggers.",
    },
    {
      q: "Are these recipes low-FODMAP for IBS?",
      a: "Our IBS-friendly collection focuses on low-FODMAP ingredients and portion sizes commonly tolerated during IBS flare-ups. Individual tolerance varies &mdash; reintroduce foods slowly and work with a registered dietitian when possible.",
    },
    {
      q: "Is GentleGourmet medical advice?",
      a: "No. GentleGourmet shares general, educational recipes and cooking guidance only. It is not a substitute for personalised medical care. Always consult your doctor or registered dietitian before making significant dietary changes.",
    },
    {
      q: "Can I search without picking a category?",
      a: "Yes. Use the search bar with or without a condition filter &mdash; keyword, category, or both work.",
    },
    {
      q: "Do you have recipes for silent reflux (LPR) and Barrett's esophagus?",
      a: "Yes. Our GERD-Friendly and Barrett's Esophagus collections share the same low-acid, low-fat principles that are typically recommended for LPR and Barrett's management.",
    },
  ];

  const jsonLdFAQ = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <link rel="canonical" href={seoData.canonicalUrl} />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <meta name="author" content={seoData.brand} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin=""
        />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,600;0,9..144,700;1,9..144,500&family=Inter:wght@400;500;600;700&display=swap"
        />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content={seoData.ogImage} />
        <meta property="og:url" content={seoData.canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={seoData.siteName} />
        <meta property="og:locale" content={seoData.locale} />
        <meta
          property="og:image:alt"
          content="Low-acid, digestive-friendly recipes by GentleGourmet"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="twitter:image" content={seoData.ogImage} />
        <meta name="twitter:site" content={seoData.twitterHandle} />
        <meta name="application-name" content={seoData.siteName} />
        <meta name="apple-mobile-web-app-title" content={seoData.siteName} />
        <link rel="alternate" hrefLang="en-us" href={seoData.canonicalUrl} />
        <link rel="alternate" hrefLang="x-default" href={seoData.canonicalUrl} />
        <script type="application/ld+json">
          {JSON.stringify(jsonLdWebsite)}
        </script>
        <script type="application/ld+json">{JSON.stringify(jsonLdWebPage)}</script>
        <script type="application/ld+json">
          {JSON.stringify(jsonLdOrganization)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(jsonLdBreadcrumbs)}
        </script>
        <script type="application/ld+json">{JSON.stringify(jsonLdItemList)}</script>
        <script type="application/ld+json">{JSON.stringify(jsonLdFAQ)}</script>
      </Helmet>

      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:z-50 focus:top-3 focus:left-3 focus:bg-white focus:text-stone-900 focus:ring-2 focus:ring-stone-900 focus:px-4 focus:py-2 focus:rounded-md"
      >
        Skip to content
      </a>

      <div
        className="min-h-screen bg-stone-50 text-stone-900"
        style={{ fontFamily: "Inter, system-ui, sans-serif" }}
      >
        <style>{`
          .font-serif-display {
            font-family: "Fraunces", "Playfair Display", Georgia, serif;
            font-feature-settings: "ss01";
            letter-spacing: -0.01em;
          }
        `}</style>

        {status === "error" && (
          <Snackbar
            anchorOrigin={{ horizontal: "center", vertical: "top" }}
            open={handleSnackbarValue}
            autoHideDuration={6000}
            onClose={handleSnackBarClose}
          >
            <Alert
              severity="error"
              onClose={handleSnackBarClose}
              sx={{ width: "100%" }}
            >
              Something went wrong. Please try again later.
            </Alert>
          </Snackbar>
        )}
        {status === "success" && (
          <Snackbar
            anchorOrigin={{ horizontal: "center", vertical: "top" }}
            open={handleSnackbarValue}
            autoHideDuration={6000}
            onClose={handleSnackBarClose}
          >
            <Alert
              severity="success"
              onClose={handleSnackBarClose}
              sx={{ width: "100%" }}
            >
              Changes to profile have been saved successfully!
            </Alert>
          </Snackbar>
        )}

        <main id="main-content">
          {/* HERO */}
          <header role="banner" className="relative bg-white border-b border-stone-200">
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-12 sm:pb-16 text-center">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-emerald-800">
                Digestive-friendly recipes
              </p>

              <h1 className="font-serif-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-semibold text-stone-900 leading-[1.05]">
                Low-acid cooking that still feels&nbsp;
                <em className="not-italic text-emerald-800">like a real dinner.</em>
              </h1>

              <p className="mt-6 text-lg text-stone-600 max-w-2xl mx-auto leading-relaxed">
                Recipes built around <strong className="font-semibold text-stone-800">GERD</strong>,{" "}
                <strong className="font-semibold text-stone-800">acid reflux</strong>,{" "}
                <strong className="font-semibold text-stone-800">IBS</strong>,{" "}
                <strong className="font-semibold text-stone-800">gastritis</strong>, and{" "}
                <strong className="font-semibold text-stone-800">Barrett's esophagus</strong> &mdash;
                without sacrificing flavour.
              </p>

              <div className="mt-10 max-w-2xl mx-auto">
                <FoodSearch />
              </div>

              <div className="mt-8 flex items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={scrollToRecipes}
                  className="text-sm font-medium text-stone-700 hover:text-stone-900 underline underline-offset-4 decoration-stone-300 hover:decoration-stone-600"
                >
                  Browse recipes by condition ↓
                </button>
              </div>
            </div>
          </header>

          {/* EDITORIAL COLLECTIONS */}
          <section
            aria-labelledby="collections-heading"
            className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16"
          >
            <div className="flex items-end justify-between mb-8 border-b border-stone-200 pb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-stone-500">
                  Collections
                </p>
                <h2
                  id="collections-heading"
                  className="font-serif-display mt-2 text-3xl sm:text-4xl font-semibold text-stone-900"
                >
                  Start here
                </h2>
              </div>
              <Link
                to="/search"
                reloadDocument
                className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-stone-700 hover:text-stone-900"
              >
                All recipes <ArrowRight size={14} aria-hidden />
              </Link>
            </div>

            <ul className="grid gap-px bg-stone-200 sm:grid-cols-2 lg:grid-cols-4 border border-stone-200 rounded-lg overflow-hidden">
              {editorialCollections.map((collection) => (
                <li key={collection.title}>
                  <Link
                    reloadDocument
                    to={collection.href}
                    className="group flex h-full flex-col bg-white p-6 hover:bg-stone-50 transition-colors"
                  >
                    <div className="text-emerald-800">{collection.icon}</div>
                    <h3 className="font-serif-display mt-4 text-xl font-semibold text-stone-900 leading-snug">
                      {collection.title}
                    </h3>
                    <p className="mt-2 text-sm text-stone-600 leading-relaxed flex-1">
                      {collection.copy}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-stone-900 group-hover:gap-2 transition-all">
                      View collection <ArrowRight size={14} aria-hidden />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>

          {/* CATEGORY SECTIONS */}
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pb-20">
            {recipeCategories.map((categoryData, index) => (
              <section
                key={categoryData.id}
                id={index === 0 ? "recipes-start" : undefined}
                className={index > 0 ? "mt-24" : "mt-4"}
                aria-labelledby={`${categoryData.id}-heading`}
              >
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 border-b border-stone-200 pb-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-stone-500">
                      Category {String(index + 1).padStart(2, "0")}
                    </p>
                    <h2
                      id={`${categoryData.id}-heading`}
                      className="font-serif-display mt-2 text-3xl sm:text-4xl font-semibold text-stone-900"
                    >
                      {categoryData.title}
                    </h2>
                    <p
                      className="mt-3 text-stone-600 max-w-2xl leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: categoryData.description }}
                    />
                  </div>
                  <Link
                    reloadDocument
                    to={categoryData.path}
                    className="inline-flex items-center gap-2 self-start sm:self-end rounded-full border border-stone-900 px-5 py-2 text-sm font-medium text-stone-900 hover:bg-stone-900 hover:text-white transition-colors whitespace-nowrap"
                  >
                    See all <ArrowRight size={14} aria-hidden />
                  </Link>
                </div>

                <PostsShowOnHome
                  amountOfPosts={3}
                  category={categoryData.category}
                />
              </section>
            ))}

            {/* GLOBAL FEED */}
            <section className="mt-24" aria-labelledby="global-heading">
              <div className="border-b border-stone-200 pb-4 mb-8">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-500">
                  From the community
                </p>
                <h2
                  id="global-heading"
                  className="font-serif-display mt-2 text-3xl sm:text-4xl font-semibold text-stone-900"
                >
                  Latest recipes
                </h2>
                <p className="mt-3 text-stone-600 max-w-2xl leading-relaxed">
                  Reader-submitted gentle-gourmet meals from around the world.
                </p>
              </div>
              <ShowPostInfiniteScroll />
            </section>

            {/* EDITORIAL / ABOUT */}
            <section className="mt-24" aria-labelledby="about-heading">
              <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-start">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-stone-500">
                    Our approach
                  </p>
                  <h2
                    id="about-heading"
                    className="font-serif-display mt-2 text-3xl sm:text-4xl font-semibold text-stone-900"
                  >
                    Built for people, not algorithms.
                  </h2>
                </div>
                <div className="text-stone-700 leading-relaxed space-y-4">
                  <p>
                    GentleGourmet is a reader-supported cookbook for people
                    living with reflux, IBS, gastritis and related digestive
                    conditions. Every recipe leans on the same playbook:
                    low-acid produce, gentle fats, minimal added sugar, and
                    cooking techniques that don't fight your body.
                  </p>
                  <p>
                    We don't promise miracle cures or fad diets. We write the
                    kind of dinner-menu cooking a thoughtful friend who happens
                    to have GERD would make &mdash; and share the notes so you
                    can adapt each dish to what <em>you</em> tolerate.
                  </p>
                </div>
              </div>
            </section>

            {/* FAQ */}
            <section className="mt-24" aria-labelledby="faq-heading">
              <div className="border-b border-stone-200 pb-4 mb-8">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-500">
                  Questions
                </p>
                <h2
                  id="faq-heading"
                  className="font-serif-display mt-2 text-3xl sm:text-4xl font-semibold text-stone-900"
                >
                  Frequently asked
                </h2>
              </div>
              <dl className="divide-y divide-stone-200 border border-stone-200 rounded-lg bg-white">
                {faqItems.map((f) => (
                  <details
                    key={f.q}
                    className="group p-6 [&_summary::-webkit-details-marker]:hidden"
                  >
                    <summary className="flex cursor-pointer items-start justify-between gap-4">
                      <dt className="font-serif-display text-lg font-semibold text-stone-900 leading-snug">
                        {f.q}
                      </dt>
                      <span
                        aria-hidden
                        className="mt-1 text-stone-400 transition-transform group-open:rotate-45"
                      >
                        +
                      </span>
                    </summary>
                    <dd
                      className="mt-3 text-stone-700 leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: f.a }}
                    />
                  </details>
                ))}
              </dl>
            </section>

            {/* CTA */}
            <section className="mt-24" aria-labelledby="cta-heading">
              <div className="rounded-lg border border-stone-200 bg-white p-10 sm:p-14 text-center">
                <h2
                  id="cta-heading"
                  className="font-serif-display text-3xl sm:text-4xl font-semibold text-stone-900"
                >
                  Cook gently with us.
                </h2>
                <p className="mt-4 text-stone-600 max-w-xl mx-auto leading-relaxed">
                  Save favourites, personalise recommendations, and share your
                  own digestive-friendly creations with the community.
                </p>
                <div className="mt-8 flex flex-wrap justify-center gap-3">
                  <Link
                    reloadDocument
                    to="/sign_up"
                    className="inline-flex items-center gap-2 rounded-full bg-stone-900 text-white px-6 py-3 font-medium hover:bg-stone-800 transition-colors"
                  >
                    Create a free account
                    <ArrowRight size={16} aria-hidden />
                  </Link>
                  <Link
                    reloadDocument
                    to="/pages/our-vision"
                    className="inline-flex items-center gap-2 rounded-full border border-stone-300 px-6 py-3 font-medium text-stone-900 hover:bg-stone-50 transition-colors"
                  >
                    Read our vision
                  </Link>
                </div>
              </div>
            </section>
          </div>
        </main>

        <SiteFooter />
      </div>
    </>
  );
}

export default Home;
